import React, { FC, ReactElement } from 'react';
import Link from '@mui/material/Link';
import { Theme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import MenuItem from '@mui/material/MenuItem';
import { clsx } from 'clsx';
// Component
import Text from '../text';
import AvatarText from '../avatars';
import menuContent, { MenuInfos } from './menuContent';
import Space, { Type } from '../layout/space';
import WickedImage from '../events/wicked/WickedImage';
import AppearanceMenuItem from './appearanceMenuItem';
// Resources
import { stringsRes } from '../../resources/strings';
import { isExternalUrl } from '../../utils/redirect';
import { ImageEvent } from '../../resources/images';
import { useEvent } from '../../hooks/useEvent';
import { EventType } from '../../core/apiTypes';

interface DrawerMenuProps {
    onClick?: (location: string) => void;
    noAdmin?: boolean;
}

const DrawerMenu: FC<DrawerMenuProps> = (props): ReactElement => {
    const { onClick, noAdmin = false } = props;
    const { pathname } = window.location;
    const { currentEvent } = useEvent();
    const isWicked = currentEvent === EventType.WICKED;

    const menuEntries: MenuInfos[] = menuContent.menuBuilder({});
    const menuAdminEntries: MenuInfos[] = menuContent.menuAdminBuilder({});
    const classes = useStyles();

    const renderMenu = (data: MenuInfos[]): JSX.Element[] => {
        return data.map((menu: MenuInfos): JSX.Element => {
            const isSelected = menu.href === pathname || pathname.startsWith(menu.href);
            return (
                <Link
                    {...{
                        to: onClick ? undefined : menu.href,
                        target: !onClick && isExternalUrl(menu.href) ? '_blank' : '',
                        rel: 'noopener noreferrer',
                        component: onClick ? undefined : RouterLink,
                        onClick: onClick
                            ? () => {
                                  onClick(menu.href);
                              }
                            : undefined,
                        color: 'inherit',
                        style: { textDecoration: 'none' },
                    }}
                    key={`${menu.label}-${menu.href}`}
                    underline="hover"
                >
                    <MenuItem className={isSelected ? classes.selected : classes.selectedVariant}>
                        <div className={classes.drawerMenuRowContainer}>
                            <div
                                className={clsx(
                                    classes.flexContainer,
                                    isSelected ? classes.selected : classes.notSelected,
                                )}
                            >
                                {menu.icon}
                            </div>
                            <div className={classes.content}>
                                <Text.ShortMediumText16 text={menu.label} color="currentcolor" />
                                {menu.description && (
                                    <div className={classes.descriptionContent}>
                                        <Text.Overline text={menu.description} />
                                        <Space type={Type.horizontal} value={0.5} />
                                        <AvatarText title={stringsRes.common.perso} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </MenuItem>
                </Link>
            );
        });
    };

    return (
        <div className={classes.drawerMenuRoot}>
            {renderMenu(menuEntries)}
            {!noAdmin && (
                <>
                    <hr className={classes.separator} />
                    {renderMenu(menuAdminEntries)}
                </>
            )}
            <hr className={classes.separator} />
            <AppearanceMenuItem
                variant="menuItem"
                className={clsx(classes.selectedVariant, classes.appearanceRow)}
                iconClassName={classes.appearanceIcon}
            />
            {isWicked && (
                <>
                    <div className={classes.spacer} />
                    <WickedImage type={ImageEvent.WickedMenu} />
                </>
            )}
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        drawerMenuRoot: {
            backgroundColor: theme.palette.background.default,
        },
        flexContainer: {
            display: 'flex',
        },
        notSelected: {
            // color: theme.palette.colors.icon.lightGrey,
        },
        selected: {
            color: theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.secondary.light,
            backgroundColor: theme.palette.colors.background.primaryElement,
            borderRadius: '4px 0px 0px 4px',
        },
        selectedVariant: {
            '&:hover': {
                borderRadius: '4px 0px 0px 4px',
            },
        },
        drawerMenuRowContainer: {
            flexDirection: 'row',
            display: 'flex',
            width: '100%',
            paddingTop: 20,
            paddingBottom: 20,
            alignItems: 'center',
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            marginLeft: theme.spacing(2),
        },
        descriptionContent: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
        },
        separator: {
            margin: 0,
            marginLeft: 30,
            marginRight: 20,
            // `solid`, not the <hr> default `inset`: inset draws the edge with a darkened border-color,
            // which turned Grey Chalk almost black.
            border: 0,
            borderTop: `1px solid ${theme.palette.colors.background.separator}`,
        },
        // Matches the 20px the other rows add on top of MenuItem's own 6px vertical padding
        appearanceRow: {
            paddingTop: 26,
            paddingBottom: 26,
        },
        appearanceIcon: {
            minWidth: 'inherit',
            marginRight: theme.spacing(2),
            color: 'inherit',
        },
        spacer: { display: 'flex', flex: 1, minHeight: theme.spacing(1) },
    }),
);

export default DrawerMenu;
